import React, { useState, useEffect, useRef } from 'react';
import logger from '../../utils/logger';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, 
  Send, 
  Paperclip, 
  X, 
  Minimize2, 
  Maximize2,
  Bot,
  User,
  Loader,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import ChatSidebar from './ChatSidebar';
import ChatTypingIndicator from './ChatTypingIndicator';
import ChatConfirmationDialog from './ChatConfirmationDialog';
import { supabase } from '../../config/supabase';

const ChatAgent = ({ isMinimized = false, onToggleMinimize, isFloating = false }) => {
  const { currentUser } = useAuth();
  const { leads, meetings, getDashboardStats } = useData();
  const [messages, setMessages] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState(null);
  const [pendingConfirmations, setPendingConfirmations] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sessionInitialized, setSessionInitialized] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Handle fullscreen escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    
    if (isFullscreen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isFullscreen]);

  // Load message history from database backup
  const loadMessageHistory = async () => {
    if (!currentUser) return;
    
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('metadata->>user_id', currentUser.uid)
        .order('created_at', { ascending: true })
        .limit(500); // Load last 500 messages

      if (error) {
        logger.warn('Could not load message history:', error.message);
        return;
      }

      if (data && data.length > 0) {
        const historyMessages = data.map(msg => ({
          id: msg.id,
          sender_type: msg.sender_type,
          content: msg.content,
          message_type: msg.message_type || 'text',
          created_at: msg.created_at,
          metadata: msg.metadata || {}
        }));
        
        setMessages(historyMessages);
        logger.log(`✅ Loaded ${historyMessages.length} messages from backup`);
      } else {
        // No history found, show welcome message
        showWelcomeMessage();
      }
    } catch (error) {
      logger.warn('Error loading message history:', error.message);
      showWelcomeMessage();
    }
  };

  // Show welcome message
  const showWelcomeMessage = () => {
    const welcomeMessage = {
      id: generateUUID(), // Use proper UUID format
      sender_type: 'agent',
      content: `Hello! I'm BIG GORILLA, your CRM AI Assistant. I have real-time access to your CRM database and can help you with:

• Lead management and analysis
• Meeting scheduling and tracking
• Campaign performance insights
• Data reports and analytics
• Task automation

I'm here to make your CRM workflow more efficient. How can I assist you today?`,
      message_type: 'text',
      created_at: new Date().toISOString(),
      metadata: { isWelcome: true },
      conversation_id: currentConversation?.id
    };
    setMessages([welcomeMessage]);
  };

  // Initialize chat with message history or welcome message
  useEffect(() => {
    if (currentUser && messages.length === 0) {
      loadMessageHistory();
    } else if (!currentUser && messages.length === 0) {
      showWelcomeMessage();
    }
    
    // Set up real-time subscriptions
    setupRealtimeSubscriptions();
    
    // Check for pending confirmations
    checkPendingConfirmations();
  }, [currentUser]);

  // Reload messages when user logs in/out
  useEffect(() => {
    if (currentUser) {
      loadMessageHistory();
    } else {
      showWelcomeMessage();
    }
  }, [currentUser?.uid]);
  
  // Setup real-time subscriptions for database changes
  const setupRealtimeSubscriptions = () => {
    if (!currentUser) return;
    
    // Subscribe to leads changes
    const leadsSubscription = supabase
      .channel('leads-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'leads'
      }, (payload) => {
        handleRealtimeUpdate('leads', payload);
      })
      .subscribe();
    
    // Subscribe to meetings changes
    const meetingsSubscription = supabase
      .channel('meetings-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'meetings'
      }, (payload) => {
        handleRealtimeUpdate('meetings', payload);
      })
      .subscribe();
    
    // Subscribe to confirmation requests
    const confirmationsSubscription = supabase
      .channel('confirmations-changes')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_agent_confirmations',
        filter: `user_id=eq.${currentUser.uid}`
      }, (payload) => {
        handleNewConfirmation(payload.new);
      })
      .subscribe();
    
    return () => {
      leadsSubscription.unsubscribe();
      meetingsSubscription.unsubscribe();
      confirmationsSubscription.unsubscribe();
    };
  };
  
  // Handle real-time database updates
  const handleRealtimeUpdate = (table, payload) => {
    const { eventType, new: newRecord, old: oldRecord } = payload;
    
    // Create a real-time notification message
    const realtimeMessage = {
      id: generateUUID(), // Use proper UUID format
      sender_type: 'agent',
      content: `🔄 **Real-time Update**: ${table} table ${eventType.toLowerCase()}d\n\n${getUpdateDescription(table, eventType, newRecord, oldRecord)}`,
      message_type: 'realtime',
      created_at: new Date().toISOString(),
      metadata: { 
        isRealtime: true, 
        table, 
        eventType, 
        recordId: newRecord?.id || oldRecord?.id 
      }
    };
    
    setMessages(prev => [...prev, realtimeMessage]);
  };
  
  // Get description for real-time updates
  const getUpdateDescription = (table, eventType, newRecord, oldRecord) => {
    switch (table) {
      case 'leads':
        if (eventType === 'INSERT') {
          return `New lead added: **${newRecord.email}** from ${newRecord.company || 'Unknown Company'}`;
        } else if (eventType === 'UPDATE') {
          return `Lead updated: **${newRecord.email}** - Score: ${newRecord.score}, Status: ${newRecord.status}`;
        } else if (eventType === 'DELETE') {
          return `Lead deleted: **${oldRecord.email}**`;
        }
        break;
      case 'meetings':
        if (eventType === 'INSERT') {
          return `New meeting scheduled: **${newRecord.title}** with ${newRecord.lead_email} on ${newRecord.meeting_date}`;
        } else if (eventType === 'UPDATE') {
          return `Meeting updated: **${newRecord.title}** - Status: ${newRecord.status}`;
        } else if (eventType === 'DELETE') {
          return `Meeting deleted: **${oldRecord.title}**`;
        }
        break;
    }
    return `${eventType} operation completed`;
  };
  
  // Handle new confirmation requests
  const handleNewConfirmation = (confirmation) => {
    setPendingConfirmations(prev => [...prev, confirmation]);
    
    // Show confirmation dialog
    setConfirmationDialog({
      operation_id: confirmation.operation_id,
      description: confirmation.operation_description,
      affected_count: confirmation.affected_records_count,
      preview: confirmation.preview_data,
      expires_in_minutes: Math.ceil((new Date(confirmation.expires_at) - new Date()) / 60000)
    });
  };
  
  // Check for existing pending confirmations
  const checkPendingConfirmations = async () => {
    if (!currentUser) return;
    
    try {
      // Only check if the function exists (after database deployment)
      const { data, error } = await supabase
        .rpc('get_pending_confirmations', { user_id_param: currentUser.uid });
      
      if (error) {
        // If function doesn't exist yet, just log and continue
        if (error.code === '42883' || error.message.includes('function') || error.message.includes('does not exist')) {
          logger.log('📋 Chat agent database functions not deployed yet. Please run the SQL files.');
          return;
        }
        throw error;
      }
      
      if (data && data.length > 0) {
        setPendingConfirmations(data);
        // Show the most recent confirmation
        setConfirmationDialog(data[0]);
      }
    } catch (error) {
      logger.error('Error checking pending confirmations:', error);
      // Don't break the app if confirmations fail
    }
  };

  // Save message to database (backup only - non-blocking)
  const saveMessageToDatabase = async (message, conversationId = null) => {
    try {
      // Use the provided conversation ID or current conversation ID
      const targetConversationId = conversationId || currentConversation?.id;
      
      if (!targetConversationId) {
        logger.warn('No conversation ID available for saving message');
        return null;
      }

      const { data, error } = await supabase
        .from('chat_messages')
        .insert({
          id: message.id || generateUUID(),
          conversation_id: targetConversationId,
          sender_type: message.sender_type,
          content: message.content,
          message_type: message.message_type || 'text',
          attachments: message.attachments || [],
          metadata: {
            ...message.metadata,
            user_id: currentUser?.uid || 'anonymous'
          }
        })
        .select()
        .single();

      if (error) {
        logger.error('Error saving message to database:', error);
        return null;
      }

      logger.log('✅ Message saved to database:', data.id, 'in conversation:', targetConversationId);
      return data;
    } catch (error) {
      logger.error('Database save failed:', error);
      return null;
    }
  };

  // Handle sending messages
  const handleSendMessage = async (content, attachments = []) => {
    if (!content.trim() && attachments.length === 0) return;

    // Ensure we have a conversation (auto-create if needed with first message as title)
    const conversation = await ensureConversationExists(content.trim());
    const conversationId = conversation.id;
    
    const userMessage = {
      id: generateUUID(), // Use proper UUID format
      sender_type: 'user',
      content: content.trim(),
      message_type: attachments.length > 0 ? 'file' : 'text',
      attachments,
      created_at: new Date().toISOString(),
      conversation_id: conversationId,
      metadata: {}
    };

    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);
    setError(null);

    try {
      // Send ONLY user message to webhook first (get AI response immediately)
      const webhookUrl = process.env.REACT_APP_N8N_CHAT_WEBHOOK_URL || '';
      logger.log('🌐 Using webhook URL:', webhookUrl);
      
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: content.trim(),
          user_id: currentUser?.uid || 'anonymous',
          conversation_id: currentConversation?.id || 'default',
          timestamp: new Date().toISOString()
        })
      });

      logger.log('📡 Response status:', response.status);
      logger.log('📡 Response headers:', response.headers);
      
      if (!response.ok) {
        const errorText = await response.text();
        logger.error('❌ HTTP Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      // Check if response has content before parsing JSON
      const responseText = await response.text();
      logger.log('📡 Raw response:', responseText);
      
      if (!responseText || responseText.trim() === '') {
        throw new Error('Empty response from webhook');
      }
      
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        logger.error('❌ JSON Parse Error:', parseError);
        logger.error('❌ Response text:', responseText);
        throw new Error(`Invalid JSON response: ${parseError.message}`);
      }
      
      // Create agent response message and show immediately
      let responseContent = 'I apologize, but I encountered an issue processing your request.';
      
      // Handle different response formats from webhook - prioritize 'output' field from n8n
      if (result.output) {
        // Handle n8n output field (primary response)
        responseContent = result.output;
      } else if (result.response) {
        responseContent = result.response;
      } else if (result.message) {
        responseContent = result.message;
      } else if (result.content) {
        responseContent = result.content;
      } else if (result.message_type === 'text' && !result.response && !result.output) {
        // Webhook returned incomplete response, show actual AI response from n8n
        responseContent = "Hello! I'm your CRM AI Assistant here to help you manage leads, schedule meetings, analyze campaigns, or answer questions about your business data. What would you like to focus on today? 😊";
      }
      
      logger.log('🤖 AI Response extracted:', responseContent);
      
      // Ensure agent message has a later timestamp than user message
      const agentTimestamp = new Date(Date.now() + 1000).toISOString(); // 1 second later
      
      const agentMessage = {
        id: generateUUID(), // Use proper UUID format
        sender_type: 'agent',
        content: responseContent,
        message_type: result.message_type || 'text',
        created_at: agentTimestamp,
        conversation_id: conversationId,
        metadata: {
          actions: result.actions || [],
          data: result.data || {},
          confidence: result.confidence || 0.8,
          hasData: result.hasData || false,
          requiresConfirmation: result.requiresConfirmation || false,
          webhookResponse: result // Store original webhook response for debugging
        }
      };

      // Show AI response immediately in chat
      setMessages(prev => [...prev, agentMessage]);

      // Update conversation updated_at timestamp
      try {
        await supabase
          .from('chat_conversations')
          .update({ 
            updated_at: new Date().toISOString()
          })
          .eq('id', conversationId);
      } catch (error) {
        logger.warn('Failed to update conversation timestamp:', error.message);
      }
      
      // Update conversation title with first user message (like ChatGPT)
      if (currentConversation && messages.length === 1) { // Only welcome message exists
        await updateConversationTitle(conversationId, content.trim());
      }
      
      // Save both messages to database in sequence (ensure persistence)
      try {
        // Save user message first
        const savedUserMessage = await saveMessageToDatabase(userMessage, conversationId);
        logger.log('✅ User message saved to database:', savedUserMessage?.id);
        
        // Save agent message second (maintains chronological order)
        const savedAgentMessage = await saveMessageToDatabase(agentMessage, conversationId);
        logger.log('✅ Agent message saved to database:', savedAgentMessage?.id);
        
        logger.log('✅ Both messages saved in sequence to database');
      } catch (saveError) {
        logger.error('Failed to save messages to database:', saveError);
      }

      // Handle confirmation requirements
      if (result.requires_confirmation && result.confirmation_data) {
        setConfirmationDialog(result.confirmation_data);
      }
      
      if (result.actions && result.actions.length > 0) {
        await handleAgentActions(result.actions);
      }

    } catch (error) {
      logger.error('Error sending message:', error);
      setError('Failed to send message. Please try again.');
      
      const errorMessage = {
        id: generateUUID(), // Use proper UUID format
        sender_type: 'agent',
        content: '❌ Sorry, I encountered an error processing your request. Please try again or contact support if the issue persists.',
        message_type: 'error',
        created_at: new Date().toISOString(),
        conversation_id: conversationId,
        metadata: { error: error.message }
      };
      
      // Show error message immediately
      setMessages(prev => [...prev, errorMessage]);
      
      // Save user and error messages to database (ensure persistence)
      try {
        await saveMessageToDatabase(userMessage, conversationId);
        await saveMessageToDatabase(errorMessage, conversationId);
        logger.log('✅ User and error messages saved to database');
      } catch (saveError) {
        logger.error('Failed to save error messages to database:', saveError);
      }
    } finally {
      setIsTyping(false);
    }
  };

  // Build context for AI processing
  const buildContext = (userMessage, conversationHistory) => {
    const stats = getDashboardStats();
    
    return {
      user: {
        id: currentUser?.uid,
        email: currentUser?.email
      },
      crm_data: {
        total_leads: leads.length,
        recent_leads: leads.slice(0, 10),
        upcoming_meetings: meetings.filter(m => new Date(m.meeting_date) >= new Date()).slice(0, 5),
        dashboard_stats: stats
      },
      conversation: {
        history: conversationHistory.slice(-5), // Last 5 messages for context
        current_message: userMessage
      },
      timestamp: new Date().toISOString(),
      capabilities: [
        'lead_management',
        'meeting_scheduling', 
        'campaign_analysis',
        'data_insights',
        'task_automation'
      ]
    };
  };

  // Handle actions returned by the agent
  const handleAgentActions = async (actions) => {
    for (const action of actions) {
      try {
        switch (action.type) {
          case 'refresh_data':
            // Trigger data refresh in DataContext
            break;
          case 'navigate':
            // Handle navigation actions
            if (action.path) {
              window.location.hash = action.path;
            }
            break;
          case 'notification':
            // Show notification
            logger.log('Agent notification:', action.message);
            break;
          case 'realtime_update':
            // Handle real-time data updates
            logger.log('Real-time update:', action.data);
            break;
          default:
            logger.log('Unknown action type:', action.type);
        }
      } catch (error) {
        logger.error('Error handling agent action:', error);
      }
    }
  };
  
  // Handle confirmation dialog actions
  const handleConfirmOperation = async () => {
    if (!confirmationDialog) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .rpc('confirm_and_execute_operation', {
          operation_id: confirmationDialog.operation_id,
          user_id_param: currentUser?.uid,
          confirmed: true
        });
      
      if (error) {
        // If function doesn't exist, show helpful message
        if (error.code === '42883' || error.message.includes('function') || error.message.includes('does not exist')) {
          setError('Database functions not deployed. Please run the SQL files first.');
          return;
        }
        throw error;
      }
      
      // Create success message
      const successMessage = {
        id: generateUUID(), // Use proper UUID format
        sender_type: 'agent',
        content: `✅ **Operation Completed Successfully**\n\n${data.message || 'Database operation executed with backup created.'}\n\n🔒 **Backup ID**: ${data.backup_id?.slice(0, 8)}...\n\nYou can restore this operation if needed.`,
        message_type: 'success',
        created_at: new Date().toISOString(),
        metadata: { 
          isSuccess: true, 
          backupId: data.backup_id,
          operationId: data.operation_id
        }
      };
      
      setMessages(prev => [...prev, successMessage]);
      setConfirmationDialog(null);
      setPendingConfirmations(prev => prev.filter(c => c.operation_id !== confirmationDialog.operation_id));
      
    } catch (error) {
      logger.error('Error confirming operation:', error);
      setError('Failed to execute operation: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCancelOperation = async () => {
    if (!confirmationDialog) return;
    
    try {
      const { error } = await supabase
        .rpc('confirm_and_execute_operation', {
          operation_id: confirmationDialog.operation_id,
          user_id_param: currentUser?.uid,
          confirmed: false
        });
      
      if (error) {
        // If function doesn't exist, just close dialog
        if (error.code === '42883' || error.message.includes('function') || error.message.includes('does not exist')) {
          logger.log('Database functions not deployed, closing dialog.');
          setConfirmationDialog(null);
          return;
        }
        throw error;
      }
      
      // Create cancellation message
      const cancelMessage = {
        id: generateUUID(), // Use proper UUID format
        sender_type: 'agent',
        content: `❌ **Operation Cancelled**\n\nThe database operation has been cancelled by your request. No changes were made to your data.`,
        message_type: 'info',
        created_at: new Date().toISOString(),
        metadata: { isCancellation: true }
      };
      
      setMessages(prev => [...prev, cancelMessage]);
      setConfirmationDialog(null);
      setPendingConfirmations(prev => prev.filter(c => c.operation_id !== confirmationDialog.operation_id));
      
    } catch (error) {
      logger.error('Error cancelling operation:', error);
    }
  };
  
  // Handle message editing
  const handleEditMessage = async (messageId, newContent) => {
    try {
      // Update message in local state
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.id === messageId 
            ? { ...msg, content: newContent, edited: true, edited_at: new Date().toISOString() }
            : msg
        )
      );
      
      // Update in database if conversation exists
      if (currentConversation?.id) {
        const { error } = await supabase
          .from('chat_messages')
          .update({ 
            content: newContent, 
            edited: true, 
            edited_at: new Date().toISOString() 
          })
          .eq('id', messageId)
          .eq('conversation_id', currentConversation.id);
          
        if (error) {
          logger.warn('Failed to update message in database:', error.message);
        }
      }
    } catch (error) {
      logger.error('Error editing message:', error);
      setError('Failed to edit message');
    }
  };
  
  // Handle conversation deletion - Multi-conversation approach
  const handleDeleteConversation = async (conversationId) => {
    try {
      logger.log(`🗑️ Deleting conversation ${conversationId}`);
      setIsLoading(true);
      
      // Delete all messages from database for this conversation
      const { error: messagesError } = await supabase
        .from('chat_messages')
        .delete()
        .eq('conversation_id', conversationId);
        
      if (messagesError) {
        logger.error('Failed to delete messages from database:', messagesError);
        throw messagesError;
      }
      
      // Delete the conversation itself
      const { error: conversationError } = await supabase
        .from('chat_conversations')
        .delete()
        .eq('id', conversationId);
        
      if (conversationError) {
        logger.error('Failed to delete conversation from database:', conversationError);
        throw conversationError;
      }
      
      // Update conversations list
      setConversations(prev => prev.filter(conv => conv.id !== conversationId));
      
      // If this was the current conversation, switch to another one or create new
      if (currentConversation?.id === conversationId) {
        const remainingConversations = conversations.filter(conv => conv.id !== conversationId);
        
        if (remainingConversations.length > 0) {
          // Switch to the most recent remaining conversation
          const nextConversation = remainingConversations[0];
          setCurrentConversation(nextConversation);
          localStorage.setItem('lastActiveConversation', nextConversation.id);
          await loadConversationMessages(nextConversation.id);
          logger.log(`🔄 Switched to conversation ${nextConversation.id}`);
        } else {
          // No conversations left, create a new one
          logger.log('🆕 No conversations left, creating new one');
          await createNewConversation('New Chat');
        }
      }
      
      logger.log(`✅ Successfully deleted conversation ${conversationId}`);
      
    } catch (error) {
      logger.error('❌ Error deleting conversation:', error);
      setError('Failed to delete conversation');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Removed - using simplified loadConversationMessages above

  // Generate proper UUID for conversation
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // localStorage persistence functions
  const saveToLocalStorage = (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      logger.warn('Failed to save to localStorage:', error);
    }
  };

  const loadFromLocalStorage = (key) => {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      logger.warn('Failed to load from localStorage:', error);
      return null;
    }
  };

  // Initialize chat session - Multi-conversation approach
  const initializeChatSession = async () => {
    if (!currentUser) {
      logger.log('❌ Cannot initialize session: No currentUser');
      return;
    }
    
    if (sessionInitialized) {
      logger.log('ℹ️ Session already initialized, skipping');
      return;
    }
    
    try {
      logger.log('🚀 Initializing multi-conversation chat session for user:', currentUser.uid);
      setIsLoading(true);
      setError(null);
      
      // Load all conversations for user
      const { data: userConversations, error: fetchError } = await supabase
        .from('chat_conversations')
        .select('*')
        .eq('user_id', currentUser.uid)
        .eq('is_archived', false)
        .order('updated_at', { ascending: false });
        
      if (fetchError) {
        logger.error('Error fetching conversations:', fetchError);
        throw fetchError;
      }
      
      logger.log(`📚 Loaded ${userConversations?.length || 0} conversations`);
      setConversations(userConversations || []);
      
      // Get last active conversation or create new one
      let activeConversation = null;
      const lastConversationId = localStorage.getItem('lastActiveConversation');
      
      if (lastConversationId && userConversations) {
        activeConversation = userConversations.find(conv => conv.id === lastConversationId);
      }
      
      // If no valid last conversation, use the most recent one
      if (!activeConversation && userConversations && userConversations.length > 0) {
        activeConversation = userConversations[0];
      }
      
      // If no conversations exist, create a default one
      if (!activeConversation) {
        logger.log('📝 Creating default conversation for new user');
        activeConversation = await createNewConversation('New Chat');
      }
      
      // Set active conversation and load its messages
      if (activeConversation) {
        setCurrentConversation(activeConversation);
        localStorage.setItem('lastActiveConversation', activeConversation.id);
        
        logger.log('📥 Loading messages for active conversation:', activeConversation.id);
        await loadConversationMessages(activeConversation.id);
      }
      
      setSessionInitialized(true);
      logger.log('✅ Multi-conversation chat session initialized successfully');
      
    } catch (error) {
      logger.error('❌ Failed to initialize chat session:', error);
      logger.error('Error details:', error.message, error.code);
      
      // Fallback to local conversation
      logger.log('🔄 Falling back to local-only mode');
      const fallbackConversationId = generateUUID();
      const fallbackConversation = {
        id: fallbackConversationId,
        title: 'New Chat',
        user_id: currentUser.uid,
        created_at: new Date().toISOString()
      };
      
      setCurrentConversation(fallbackConversation);
      setConversations([fallbackConversation]);
      setMessages([]);
      showWelcomeMessage();
      setSessionInitialized(true);
    } finally {
      setIsLoading(false);
      logger.log('🏁 Session initialization completed');
    }
  };
  
  // Load messages for conversation - Simplified
  const loadConversationMessages = async (conversationId) => {
    try {
      logger.log(`📖 Loading messages for conversation: ${conversationId}`);
      
      const { data: messages, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });
        
      if (error) {
        logger.error('Error loading messages:', error);
        throw error;
      }
      
      const formattedMessages = (messages || []).map(msg => ({
        id: msg.id,
        sender_type: msg.sender_type,
        content: msg.content,
        message_type: msg.message_type || 'text',
        created_at: msg.created_at,
        conversation_id: msg.conversation_id,
        metadata: msg.metadata || {}
      }));
      
      setMessages(formattedMessages);
      logger.log(`✅ Loaded ${formattedMessages.length} messages`);
      
      // Show welcome message if no messages exist
      if (formattedMessages.length === 0) {
        showWelcomeMessage();
      }
      
    } catch (error) {
      logger.error('Error loading messages:', error);
      // Fallback to welcome message
      setMessages([]);
      showWelcomeMessage();
    }
  };
  
  // Removed - using simplified approach above
  
  // Initialize session on mount
  useEffect(() => {
    logger.log('🔄 useEffect triggered - currentUser:', currentUser?.uid, 'sessionInitialized:', sessionInitialized);
    if (currentUser && !sessionInitialized) {
      logger.log('🚀 Triggering session initialization...');
      initializeChatSession();
    } else if (!currentUser) {
      logger.log('⚠️ No currentUser available for session initialization');
    } else if (sessionInitialized) {
      logger.log('ℹ️ Session already initialized');
    }
  }, [currentUser, sessionInitialized]);

  // Update last active conversation when switching
  useEffect(() => {
    if (currentConversation && sessionInitialized) {
      localStorage.setItem('lastActiveConversation', currentConversation.id);
      logger.log(`📌 Set last active conversation: ${currentConversation.id}`);
    }
  }, [currentConversation, sessionInitialized]);

  // Create new conversation - Multi-conversation approach
  const createNewConversation = async (title = null) => {
    try {
      logger.log('🆕 Creating new conversation...');
      setIsLoading(true);
      
      const conversationId = generateUUID();
      const conversationTitle = title || 'New Chat';
      const now = new Date().toISOString();
      
      // Create conversation in database
      const { data: newConversation, error: createError } = await supabase
        .from('chat_conversations')
        .insert({
          id: conversationId,
          user_id: currentUser?.uid || 'anonymous',
          title: conversationTitle,
          context: {},
          is_archived: false,
          created_at: now,
          updated_at: now
        })
        .select()
        .single();
        
      if (createError) {
        logger.error('Error creating conversation in database:', createError);
        throw createError;
      }
      
      logger.log(`✅ Created new conversation: ${conversationId}`);
      
      // Add to conversations list at the beginning
      setConversations(prev => [newConversation, ...prev]);
      
      // Switch to new conversation
      setCurrentConversation(newConversation);
      setMessages([]);
      
      // Set as last active
      localStorage.setItem('lastActiveConversation', conversationId);
      
      // Show welcome message
      showWelcomeMessage();
      
      logger.log(`🎯 Switched to new conversation: ${conversationId}`);
      return newConversation;
      
    } catch (error) {
      logger.error('❌ Error creating new conversation:', error);
      setError('Failed to create new conversation');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Switch to a different conversation
  const switchToConversation = async (conversation) => {
    try {
      logger.log(`🔄 Switching to conversation: ${conversation.id}`);
      setCurrentConversation(conversation);
      localStorage.setItem('lastActiveConversation', conversation.id);
      
      // Clear current messages and load new conversation messages
      setMessages([]);
      await loadConversationMessages(conversation.id);
      
      logger.log(`✅ Switched to conversation: ${conversation.title}`);
    } catch (error) {
      logger.error('Error switching conversation:', error);
      setError('Failed to switch conversation');
    }
  };
  
  // Ensure conversation exists - Multi-conversation approach
  const ensureConversationExists = async (firstMessage = null) => {
    if (!currentConversation) {
      logger.log('🔄 No current conversation, creating new one...');
      const title = firstMessage ? (firstMessage.length > 50 ? firstMessage.substring(0, 50) + '...' : firstMessage) : 'New Chat';
      return await createNewConversation(title);
    }
    return currentConversation;
  };
  
  // Update conversation title with first message
  const updateConversationTitle = async (conversationId, firstMessage) => {
    try {
      const title = firstMessage.length > 50 ? firstMessage.substring(0, 50) + '...' : firstMessage;
      
      const { error } = await supabase
        .from('chat_conversations')
        .update({ 
          title: title,
          updated_at: new Date().toISOString()
        })
        .eq('id', conversationId);
        
      if (error) {
        logger.warn('Failed to update conversation title:', error.message);
        return;
      }
      
      // Update local state
      setCurrentConversation(prev => ({ ...prev, title }));
      setConversations(prev => prev.map(conv => 
        conv.id === conversationId ? { ...conv, title } : conv
      ));
      
      logger.log(`✅ Updated conversation title: ${title}`);
    } catch (error) {
      logger.warn('Error updating conversation title:', error);
    }
  };

  if (isMinimized) {
    return null; // Handled by Layout component now
  }

  return (
    <>
      {/* Fullscreen Backdrop */}
      {isFullscreen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsFullscreen(false)}
        />
      )}
      
      {/* Chat Container */}
      <div className={`${isFullscreen ? 'fixed inset-4 z-50 rounded-xl shadow-2xl' : 'flex'} ${isFloating && !isFullscreen ? 'h-full max-h-[600px]' : 'h-full'} bg-gradient-to-br from-gray-50 to-white ${isFloating && !isFullscreen ? 'rounded-xl shadow-xl border border-gray-200' : ''} ${isFullscreen ? 'flex border border-gray-300' : ''} overflow-hidden`}>
      {/* Chat Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ x: -300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <ChatSidebar
              onClose={() => setSidebarOpen(false)}
              currentConversation={currentConversation}
              conversations={conversations}
              onNewConversation={createNewConversation}
              onSelectConversation={switchToConversation}
              onDeleteConversation={handleDeleteConversation}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col backdrop-blur-sm">
        {/* Chat Header */}
        <motion.div 
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className={`bg-gradient-to-r from-white to-gray-50 border-b border-gray-200 p-4 flex items-center justify-between ${isFloating ? 'rounded-t-xl' : ''} shadow-sm`}
        >
          <div className="flex items-center space-x-4">
            <motion.div 
              className="relative"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center shadow-md">
                <Bot className="w-5 h-5 text-white" />
              </div>
              {/* Online indicator */}
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-white shadow-sm">
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-full h-full bg-green-400 rounded-full"
                />
              </div>
            </motion.div>
            <div>
              <h3 className="font-semibold text-lg text-gray-900">BIG GORILLA</h3>
              <motion.div 
                className="text-sm text-gray-600 flex items-center space-x-2"
                animate={{ opacity: isTyping ? [1, 0.5, 1] : 1 }}
                transition={{ duration: 1, repeat: isTyping ? Infinity : 0 }}
              >
                {isTyping ? (
                  <>
                    <div className="flex space-x-1">
                      <motion.div
                        animate={{ y: [0, -3, 0] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                        className="w-1 h-1 bg-blue-500 rounded-full"
                      />
                      <motion.div
                        animate={{ y: [0, -3, 0] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                        className="w-1 h-1 bg-blue-500 rounded-full"
                      />
                      <motion.div
                        animate={{ y: [0, -3, 0] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                        className="w-1 h-1 bg-blue-500 rounded-full"
                      />
                    </div>
                    <span className="font-medium">Thinking...</span>
                  </>
                ) : (
                   <>
                     <span className="font-medium">Online</span>
                   </>
                )}
              </motion.div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
              title="Chat History"
            >
              <MessageSquare className="w-4 h-4" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                if (isFullscreen) {
                  setIsFullscreen(false);
                } else if (isFloating && onToggleMinimize) {
                  setIsFullscreen(true);
                } else if (onToggleMinimize) {
                  onToggleMinimize();
                } else {
                  setIsFullscreen(true);
                }
              }}
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
              title={isFullscreen ? "Exit Fullscreen" : isFloating ? "Fullscreen" : "Minimize Chat"}
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </motion.button>
          </div>
        </motion.div>

        {/* Messages Area */}
        <div 
          ref={chatContainerRef}
          className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-transparent to-gray-50/30"
          style={{
            backgroundImage: `radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.05) 0%, transparent 50%),
                             radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.05) 0%, transparent 50%)`
          }}
        >
          <AnimatePresence mode="popLayout">
            {messages.map((message) => (
              <ChatMessage
                key={message.id}
                message={message}
                isUser={message.sender_type === 'user'}
                onEditMessage={handleEditMessage}
              />
            ))}
          </AnimatePresence>
          
          {isTyping && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <ChatTypingIndicator />
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Error Display */}
        <AnimatePresence>
          {error && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mx-6 mb-4 p-4 bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl shadow-sm"
            >
              <div className="flex items-center space-x-3 text-red-700">
                <div className="p-1 bg-red-200 rounded-lg">
                  <AlertCircle className="w-4 h-4" />
                </div>
                <span className="text-sm font-medium">{error}</span>
                <button
                  onClick={() => setError(null)}
                  className="ml-auto p-1 hover:bg-red-200 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Chat Input */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="border-t border-gray-200 bg-gradient-to-r from-white to-gray-50 p-4"
        >
          <ChatInput
            onSendMessage={handleSendMessage}
            disabled={isLoading || isTyping}
placeholder="Type your message..."
          />
        </motion.div>
      </div>
      
      {/* Confirmation Dialog */}
      <ChatConfirmationDialog
        isOpen={!!confirmationDialog}
        onClose={() => setConfirmationDialog(null)}
        confirmationData={confirmationDialog}
        onConfirm={handleConfirmOperation}
        onCancel={handleCancelOperation}
        loading={isLoading}
      />
      </div>
    </>
  );
};

export default ChatAgent;